const admin = {
  accessDenied: {
    title: 'Access denied',
    description: 'This area is restricted to administrators only.',
  },
  title: 'Admin dashboard',
  tabs: {
    sessions: 'Sessions',
    traces: 'Traces',
    costSummary: 'Cost summary',
    analytics: 'Analytics',
    feedback: 'Feedback',
    reports: 'Reports',
  },
  actions: {
    close: 'Close',
    refresh: 'Refresh',
    refreshData: 'Refresh data',
    reset: 'Reset',
    resetFilters: 'Reset filters',
    resetToDefaults: 'Reset to defaults',
    previous: 'Previous',
    next: 'Next',
    details: 'Details',
    view: 'View',
    exportCsv: 'Export CSV',
    exporting: 'Exporting...',
    viewHtml: 'View HTML',
    viewPdf: 'View PDF',
    delete: 'Delete',
    deleteReport: 'Delete report',
    generateReport: 'Generate report',
    generateNewReport: 'Generate new report',
    cancel: 'Cancel',
    generating: 'Generating...',
    viewTrace: 'View trace',
    copy: 'Copy',
    copied: 'Copied!',
    full: 'Full',
    viewFullData: 'View full data',
    viewFullOutput: 'View full output',
  },
  filters: {
    dateRange: 'Date range',
    customRange: 'Custom range',
    today: 'Today',
    lastDays: 'Last {{count}} days',
    startDate: 'Start date',
    endDate: 'End date',
    customerId: 'Customer ID',
    allCustomers: 'All customers',
  },
  errors: {
    label: 'Error:',
  },
  status: {
    loading: 'Loading...',
    loadingData: 'Loading admin data',
    noData: 'No data found',
  },
  labels: {
    notAvailable: 'N/A',
  },
  sessions: {
    title: 'Chat sessions',
    excludeEmpty: 'Exclude empty sessions',
    userInitiatedOnly: 'User-initiated only',
    total: '{{count}} total sessions',
    noResults: 'No sessions found matching the filters.',
    noSessionsToExport: 'No sessions to export',
    exportFailed: 'Failed to export sessions to CSV',
    exportAll: 'Export all sessions to CSV',
    noMessages: 'No messages',
    viewAnalysis: 'View analysis with score {{score}}',
    viewLinkClicks: 'View {{count}} link clicks',
    headers: {
      user: 'User',
      customer: 'Customer',
      source: 'Source',
      firstMessage: 'First message',
      started: 'Started',
      messages: 'Messages',
      analysis: 'Analysis',
      linkClicks: 'Link clicks',
      details: 'Details',
    },
  },
  traces: {
    title: 'Message traces',
    summary: 'Showing {{visible}} of {{total}} traces',
    headers: {
      messageId: 'Message ID',
      traceId: 'Trace ID',
      createdAt: 'Created at',
      inputTokens: 'Input tokens',
      outputTokens: 'Output tokens',
      time: 'Time',
      actions: 'Actions',
    },
  },
  costSummary: {
    title: 'Cost summary',
    noData: 'No cost data available for the selected date range.',
    totalCost: 'Total cost',
    totalTokens: 'Total tokens',
    inputTokens: 'Input tokens',
    outputTokens: 'Output tokens',
    dailyBreakdown: 'Daily breakdown',
    noDailyData: 'No daily cost data available for this period.',
    headers: {
      date: 'Date',
      messages: 'Messages',
      inputTokens: 'Input tokens',
      outputTokens: 'Output tokens',
      cacheTokens: 'Cache tokens',
      cost: 'Cost',
    },
  },
  analytics: {
    title: 'Token usage analytics',
    noData: 'No analytics data available for the selected date range.',
    dateRange: 'Showing data from {{start}} to {{end}}',
    noPeriodData: 'No analytics data available for this period.',
    headers: {
      date: 'Date',
      messages: 'Messages',
      inputTokens: 'Input tokens',
      outputTokens: 'Output tokens',
      cacheTokens: 'Cache tokens',
      toolsExecuted: 'Tools executed',
      averageResponseTime: 'Average response time',
    },
  },
  feedback: {
    title: 'Message feedback',
    total: '{{count}} total feedback records',
    noResults: 'No feedback found matching the filters.',
    noSessionId: 'No session ID is available for this feedback.',
    sessionLoadFailed: 'Failed to load session details.',
    headers: {
      submitted: 'Submitted',
      rating: 'Rating',
      messagePreview: 'Message preview',
      comments: 'Comments',
      customer: 'Customer',
      user: 'User',
      session: 'Session',
    },
  },
  reports: {
    title: 'Analysis reports',
    total: '{{count}} total reports',
    deleteConfirmation: 'Are you sure you want to delete this report?',
    deleteFailed: 'Failed to delete report.',
    loading: 'Loading reports...',
    noResults: 'No reports found for the selected filters.',
    createPrompt: 'Select Generate report to create a new analysis report.',
    status: {
      completed: 'Completed',
      failed: 'Failed',
      inProgress: 'In progress',
      pending: 'Pending',
    },
    rangeType: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      custom: 'Custom',
    },
    headers: {
      id: 'ID',
      status: 'Status',
      type: 'Type',
      dateRange: 'Date range',
      sessions: 'Sessions',
      generated: 'Generated',
      actions: 'Actions',
    },
  },
  modals: {
    close: 'Close',
    identifier: 'ID: {{id}}',
    generateReport: {
      title: 'Generate analysis report',
      invalidDateRange: 'Select a valid date range.',
      generationFailed: 'Failed to generate report.',
      insufficientConfirmation: 'Only {{available}} analysis records are available (minimum {{minimum}} recommended). Continue anyway?',
      successTitle: 'Report generation started successfully!',
      successDescription: 'The report may take several minutes to generate. You can monitor progress on the Reports tab.',
      reportType: 'Report type',
      month: 'Month',
      year: 'Year',
      quarter: 'Quarter',
      quarterOption: 'Q{{quarter}} ({{start}}-{{end}})',
      startDate: 'Start date',
      endDate: 'End date',
      customerId: 'Customer ID (optional)',
      allCustomers: 'Leave blank for all customers',
      checkingAvailability: 'Checking available analysis records...',
      availability: '{{count}} analysis records available',
      limitedAvailability: 'Only {{count}} analysis records are available (minimum {{minimum}} recommended)',
    },
    sessionDetail: {
      title: 'Session details',
      overview: 'Overview',
      sessionId: 'Session ID:',
      customer: 'Customer:',
      user: 'User:',
      started: 'Started:',
      lastActivity: 'Last activity:',
      totalMessages: 'Total messages:',
      messages: 'Messages ({{count}})',
      userRole: 'User',
      assistantRole: 'Assistant',
      fleet: 'Fleet:',
      helpful: 'Helpful',
      notHelpful: 'Not helpful',
      loadingMap: 'Loading map...',
      noTrace: 'No trace data is available for this message.',
      traceLoadFailed: 'Failed to load trace details.',
      loadingTrace: 'Loading trace',
    },
    feedbackDetail: {
      title: 'Feedback details',
      overview: 'Overview',
      rating: 'Rating:',
      positive: 'Positive',
      negative: 'Negative',
      submitted: 'Submitted:',
      customer: 'Customer:',
      user: 'User:',
      sessionId: 'Session ID:',
      message: 'Message',
      comments: 'Comments',
      viewRelatedSession: 'View related session',
      noSession: 'No session ID is associated with this feedback.',
      sessionLoadFailed: 'Failed to load session details.',
    },
    linkClicks: {
      title: 'Link clicks ({{count}})',
      loadFailed: 'Failed to load link clicks.',
      loading: 'Loading link clicks...',
      noResults: 'No link clicks were recorded for this session.',
      headers: {
        userEmail: 'User email',
        url: 'URL',
        linkText: 'Link text',
        type: 'Type',
        clickedAt: 'Clicked at',
      },
    },
    traceDetail: {
      title: 'Trace details',
      overview: 'Overview',
      traceId: 'Trace ID:',
      model: 'Model:',
      customerId: 'Customer ID:',
      totalTime: 'Total time:',
      inputTokens: 'Input tokens:',
      outputTokens: 'Output tokens:',
      cacheRead: 'Cache read:',
      stopReason: 'Stop reason:',
      message: 'Message',
      events: 'Events ({{count}})',
      tool: 'Tool:',
      useId: 'Use ID:',
      inputParameters: 'Input parameters',
      output: 'Output',
      intent: 'Intent:',
      intentScore: 'Intent score:',
      contextMessagesUsed: 'Context messages used:',
      contextMessagesRendered: 'Context messages rendered:',
      contextMessageIds: 'Context message IDs ({{count}})',
      systemPrompt: 'System prompt',
      metadata: 'Metadata',
      reasoning: 'Reasoning:',
      toolExecutions: 'Tool executions ({{count}})',
      success: 'Success',
      failed: 'Failed',
      sessionDocuments: 'Session documents ({{count}})',
      sessionDocument: 'Session document',
      mapSessionDocument: 'Map session document',
      documentSessionDocument: 'Session document',
      type: 'Type:',
      summary: 'Summary:',
      documentId: 'Document ID:',
      toolUseId: 'Tool use ID:',
      created: 'Created:',
      data: 'Data',
      truncated: 'truncated',
    },
    sessionAnalysis: {
      title: 'Session analysis',
      overview: 'Analysis overview',
      overallScore: 'Overall score',
      clarityScore: 'Clarity score',
      actionabilityScore: 'Actionability score',
      responseQualityScore: 'Response quality score',
      details: 'Analysis details',
      intentSummary: 'Intent summary:',
      isFailure: 'Is failure:',
      yes: 'Yes',
      no: 'No',
      validationStatus: 'Validation status:',
      modelId: 'Model ID:',
      analysisVersion: 'Analysis version:',
      analysisTime: 'Analysis time:',
      validationMessages: 'Validation messages',
      fullJson: 'Full analysis JSON',
      noJson: 'No analysis JSON available',
    },
    toolOutput: {
      title: 'Tool output: {{name}}',
      sessionDocumentTitle: 'Session document data: {{title}}',
      copyData: 'Copy data to clipboard',
      copyOutput: 'Copy output to clipboard',
      loadingData: 'Loading data...',
      loadingOutput: 'Loading output...',
      noData: 'No data available',
      noOutput: 'No output available',
      sessionDocumentDataRequired: 'A session ID is required to fetch session document data.',
      outputRequired: 'A session ID is required to fetch output.',
      dataLoadFailed: 'Failed to load session document data.',
      outputLoadFailed: 'Failed to load output.',
    },
  },
  pagination: {
    summary: 'Showing {{start}} to {{end}} of {{total}} results',
    detailedSummary: 'Showing {{start}} to {{end}} of {{total}} results (Page {{currentPage}} of {{totalPages}})',
    pageSummary: 'Page {{currentPage}} of {{totalPages}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
  },
} as const;

export default admin;